import { useState, useCallback, useRef } from "react";
import { TrendingUp, ChevronRight, DollarSign, Loader2, RefreshCw } from "lucide-react";
import { PostLoadCard, PostLoadCardHeader } from "@/components/post-load/PostLoadCard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Carrier-side variant of AILoadRepricing.
 *
 * v1 honesty note: this card compares each posted truck's `rate_per_mile`
 * against the median rate_per_mile of OTHER active truck postings with the
 * SAME equipment_type (an equipment-level benchmark — not a true lane-market
 * model). Wording is intentionally "below benchmark" / "rate positioning
 * opportunity" rather than "below market".
 */

interface PositioningTruck {
  id: string;
  origin: string;
  destination: string;
  equipmentType: string;
  currentRpm: number;
  benchmarkRpm: number;
  deltaRpm: number;
  estDailyMiles: number;
  estUplift: number;
}

const EST_DAILY_MILES = 500;
const BELOW_BENCHMARK_PCT = 0.92; // current ≤ 92% of benchmark = flagged

export default function AITruckRatePositioning() {
  const { user } = useAuth();
  const [trucks, setTrucks] = useState<PositioningTruck[]>([]);
  const [estimatedUplift, setEstimatedUplift] = useState(0);
  const [loading, setLoading] = useState(false);
  const hasRunRef = useRef(false);

  const analyze = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    hasRunRef.current = true;
    setLoading(true);

    try {
      // 1) Pull this user's active trucks with a rate_per_mile set.
      const { data: myTrucks } = await supabase
        .from("truck_postings")
        .select("id, origin_location, destination_location, equipment_type, rate_per_mile")
        .eq("created_by", user.id)
        .in("status", ["active", "available"])
        .not("rate_per_mile", "is", null)
        .gt("rate_per_mile", 0)
        .limit(50);

      if (!myTrucks?.length) {
        setTrucks([]);
        setEstimatedUplift(0);
        return;
      }

      // 2) Pull broader pool (other carriers' active trucks) for the equipment
      //    benchmark. We exclude this user so they don't benchmark themselves.
      const equipments = Array.from(
        new Set(myTrucks.map((t: any) => t.equipment_type).filter(Boolean))
      );
      const { data: poolRows } = await supabase
        .from("truck_postings")
        .select("equipment_type, rate_per_mile")
        .in("status", ["active", "available"])
        .neq("created_by", user.id)
        .in("equipment_type", equipments as string[])
        .not("rate_per_mile", "is", null)
        .gt("rate_per_mile", 0)
        .limit(2000);

      const benchmarkByEq = new Map<string, number>();
      const grouped = new Map<string, number[]>();
      (poolRows || []).forEach((r: any) => {
        const eq = r.equipment_type;
        if (!eq) return;
        if (!grouped.has(eq)) grouped.set(eq, []);
        grouped.get(eq)!.push(Number(r.rate_per_mile));
      });
      grouped.forEach((arr, eq) => {
        arr.sort((a, b) => a - b);
        const mid = Math.floor(arr.length / 2);
        const median = arr.length % 2 === 0 ? (arr[mid - 1] + arr[mid]) / 2 : arr[mid];
        benchmarkByEq.set(eq, median);
      });

      const flagged: PositioningTruck[] = [];
      for (const t of myTrucks as any[]) {
        const benchmark = benchmarkByEq.get(t.equipment_type);
        if (!benchmark || benchmark <= 0) continue;
        const currentRpm = Number(t.rate_per_mile);
        if (currentRpm >= benchmark * BELOW_BENCHMARK_PCT) continue;

        const deltaRpm = benchmark - currentRpm;
        flagged.push({
          id: t.id,
          origin: t.origin_location || "—",
          destination: t.destination_location || "Anywhere",
          equipmentType: t.equipment_type,
          currentRpm: Math.round(currentRpm * 100) / 100,
          benchmarkRpm: Math.round(benchmark * 100) / 100,
          deltaRpm: Math.round(deltaRpm * 100) / 100,
          estDailyMiles: EST_DAILY_MILES,
          estUplift: Math.round(deltaRpm * EST_DAILY_MILES),
        });
      }

      flagged.sort((a, b) => b.estUplift - a.estUplift);
      setTrucks(flagged);
      setEstimatedUplift(flagged.reduce((s, x) => s + x.estUplift, 0));
    } catch (err) {
      console.error("AITruckRatePositioning error:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const top = trucks.slice(0, 3);

  // Demo data shown before scan or when no results
  const showDemo = !hasRunRef.current && !loading;
  const demoTrucks = [
    { lane: "Dallas, TX → Atlanta, GA", delta: 320 },
    { lane: "Chicago, IL → Anywhere", delta: 240 },
    { lane: "Phoenix, AZ → Houston, TX", delta: 175 },
  ];
  const demoUplift = 735;

  return (
    <PostLoadCard>
      <PostLoadCardHeader
        icon={<TrendingUp className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title="ATLAS Truck Rate Positioning"
        demoHintKey="section.truckRatePositioning"
        variant="atlas"
      >
        {!loading && trucks.length > 0 && (
          <button onClick={() => analyze()} className="text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw className="h-3 w-3" />
          </button>
        )}
      </PostLoadCardHeader>
      <div className="px-3 pb-3 pt-3 space-y-2.5">
        {loading ? (
          <div className="flex items-center justify-center py-4 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Analyzing posted truck rates vs equipment benchmark…</span>
          </div>
        ) : showDemo ? (
          <>
            <div className="rounded-md px-3 py-2 flex items-center gap-2" style={{ backgroundColor: "hsl(35 95% 50% / 0.10)" }}>
              <DollarSign className="h-3 w-3 shrink-0" style={{ color: "hsl(35 95% 50%)" }} />
              <span className="text-[11px] font-semibold" style={{ color: "hsl(35 80% 42%)" }}>
                3 trucks priced below benchmark
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                  +${demoUplift.toLocaleString()}
                </span>
                <span className="text-[10px] text-muted-foreground">est. daily uplift</span>
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">Top affected trucks</span>
              {demoTrucks.map((d) => (
                <div key={d.lane} className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground font-medium truncate mr-2">{d.lane}</span>
                  <span className="font-semibold shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                    +${d.delta.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={() => analyze()}
              className="flex items-center justify-center gap-2 text-[11px] font-semibold w-full rounded-md px-3 py-3 transition-colors"
              style={{
                backgroundColor: "hsl(170 85% 32% / 0.12)",
                color: "hsl(var(--brand-teal-deep))",
              }}
            >
              <TrendingUp className="h-3.5 w-3.5" />
              Scan Trucks for Rate Opportunities
            </button>
          </>
        ) : trucks.length === 0 ? (
          <p className="text-[11px] text-muted-foreground py-2">All trucks are priced at or above the equipment benchmark.</p>
        ) : (
          <>
            <div className="rounded-md px-3 py-2 flex items-center gap-2" style={{ backgroundColor: "hsl(35 95% 50% / 0.10)" }}>
              <DollarSign className="h-3 w-3 shrink-0" style={{ color: "hsl(35 95% 50%)" }} />
              <span className="text-[11px] font-semibold" style={{ color: "hsl(35 80% 42%)" }}>
                {trucks.length} truck{trucks.length !== 1 ? "s" : ""} priced below benchmark
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                  +${Math.round(estimatedUplift).toLocaleString()}
                </span>
                <span className="text-[10px] text-muted-foreground">est. daily uplift</span>
              </div>
            </div>

            {top.length > 0 && (
              <div className="space-y-1">
                <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">Top affected trucks</span>
                {top.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-[11px]">
                    <span className="text-foreground font-medium truncate mr-2">
                      {t.origin} → {t.destination}
                    </span>
                    <span className="font-semibold shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                      +${t.estUplift.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => analyze()}
              className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
              style={{
                backgroundColor: "hsl(170 85% 32% / 0.12)",
                color: "hsl(var(--brand-teal-deep))",
              }}
            >
              Re-scan Truck Rate Positioning
              <ChevronRight className="h-3 w-3 ml-auto" />
            </button>
          </>
        )}
      </div>
    </PostLoadCard>
  );
}
